/**
 * Runtime environment detection and configuration for rakeback-web.
 * Determines environment and API targets based on hostname at runtime,
 * enabling a single build artifact to work across all environments.
 */

type RuntimeEnv = 'production' | 'development' | 'preview' | 'local'

type RuntimeConfig = {
  env: RuntimeEnv
  apiBaseUrl: string
  oauthClientId: string
}

const PROD_API = 'https://rakeback-wizard.gtowizard.com'
const DEV_API = 'https://rakeback-wizard.dev.gtowizard.com'

const PROD_OAUTH_CLIENT_ID = 'qLpcn9tzkzCu4pVbJ0EiMLkJAEjpI2M9xSiEXsts'
const DEV_OAUTH_CLIENT_ID = '5EaLpuqKG7zihvXTxH1X4mkzUFXIUbM9caChfryF'

function classifyEnv(hostname: string): RuntimeEnv {
  if (hostname === 'rbwizard.com' || hostname === 'www.rbwizard.com') {
    return 'production'
  }
  if (hostname.endsWith('.dev.gtowizard.com')) {
    return 'development'
  }
  if (hostname.endsWith('.pages.dev')) {
    return 'preview'
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local'
  }

  return 'development'
}

export function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') {
    return {
      env: 'development',
      apiBaseUrl: DEV_API,
      oauthClientId: DEV_OAUTH_CLIENT_ID,
    }
  }

  const env = classifyEnv(window.location.hostname)
  const isProd = env === 'production'

  const apiBaseUrl =
    typeof import.meta.env.VITE_PROXY_URL === 'string' && import.meta.env.VITE_PROXY_URL.length > 0
      ? '' // empty string signals proxy mode (requests go through dev server)
      : isProd
        ? PROD_API
        : DEV_API

  return {
    env,
    apiBaseUrl,
    oauthClientId: isProd ? PROD_OAUTH_CLIENT_ID : DEV_OAUTH_CLIENT_ID,
  }
}

export function isProdEnv(): boolean {
  return getRuntimeConfig().env === 'production'
}

export function isDevEnv(): boolean {
  const env = getRuntimeConfig().env === 'development' || getRuntimeConfig().env === 'preview'

  return env
}

export function isLocalEnv(): boolean {
  return getRuntimeConfig().env === 'local'
}
